import React from 'react';
import { Box, Icon, Link, Title } from '@nimbus-ds/components';
import { Text } from '@nimbus-ds/components';
import { ChevronDownIcon, ChevronUpIcon, StarIcon } from '@nimbus-ds/icons';
import { PaymentProviderComparisonResult } from 'commons/types/PaymentProviderComparisonResult';
import { formatCurrency, useTranslationWithPrefix } from 'commons/utils';
import { IStoreInfo } from 'redux/slices/types';
import CalculatedResultsDetails from './CalculatedResultsDetails';

interface CalculatedResultsCardProps {
  result: PaymentProviderComparisonResult;
  storeInfo: IStoreInfo;
  isExpanded: boolean;
  onToggleDetails: () => void;
  savingsAmount: number;
  installmentsQty: number;
}

const CalculatedResultsCard = ({
  result,
  storeInfo,
  isExpanded,
  onToggleDetails,
  savingsAmount,
  installmentsQty,
}: CalculatedResultsCardProps) => {
  const { t } = useTranslationWithPrefix('landing.feeCalculatorModal');

  const isPagonube = result.gateway === 'nuvempago_commons';

  return (
    <>
      <Box
        key={result.gateway}
        backgroundColor={isPagonube ? 'primary-surface' : 'neutral-background'}
        borderRadius="2"
        borderStyle="solid"
        borderWidth="1"
        borderColor={
          isPagonube ? 'primary-surfaceHighlight' : 'neutral-surfaceHighlight'
        }
      >
        <Box padding="3">
          <Box display="flex" flexDirection="column" gap="3">
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <img
                src={`/assets/img/${result.gateway}.png`}
                alt={result.gateway}
              />
              {isPagonube && (
                <Box
                  display="flex"
                  alignItems="center"
                  gap="1"
                  padding="1"
                  backgroundColor="success-surface"
                  borderRadius="2"
                  borderStyle="solid"
                  borderWidth="1"
                  borderColor="success-surfaceHighlight"
                  color="success-textLow"
                >
                  <Icon source={<StarIcon size="small" />} />
                  <Text fontSize="caption">{t('results.bestOption')}</Text>
                </Box>
              )}
            </Box>

            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="flex-end"
            >
              <Box>
                <Text fontSize="caption" fontWeight="medium">
                  {isPagonube
                    ? t('results.youWillReceive')
                    : t('results.youWillGet')}
                </Text>
                <Title
                  as="h2"
                  color={isPagonube ? 'success-textLow' : 'neutral-textHigh'}
                >
                  {formatCurrency(
                    {
                      value: result.netAmount,
                      currency: result.currency,
                    },
                    storeInfo,
                  )}
                </Title>
              </Box>

              <Box>
                <Link
                  as="button"
                  appearance="primary"
                  onClick={onToggleDetails}
                  textDecoration="none"
                >
                  {t('results.details')}{' '}
                  <Icon
                    color="currentColor"
                    source={
                      isExpanded ? (
                        <ChevronUpIcon size="small" />
                      ) : (
                        <ChevronDownIcon size="small" />
                      )
                    }
                  />
                </Link>
              </Box>
            </Box>

            {isExpanded && (
              <CalculatedResultsDetails
                isPagoNube={isPagonube}
                results={result}
                storeInfo={storeInfo}
                savingsAmount={savingsAmount}
                installmentsQty={installmentsQty}
              />
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default CalculatedResultsCard;
